import type React from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

import { IconButton } from "@/components/Button/IconButton";
import { FormDatePicker } from "@/components/Form/FormDatePicker";
import { ClockPlusIcon } from "@/components/Icons/ClockPlusIcon";
import { Trash01Icon } from "@/components/Icons/Trash01Icon";
import { Button } from "@/design-system/Button";
import { Columns } from "@/design-system/Columns";
import { Inline } from "@/design-system/Inline";
import { Text } from "@/design-system/Text";
import { isDefined } from "@/helpers/util";
import type { BookableDay } from "@/modules/bookings/constants";
import { defaultBookableDayEndTime, defaultBookableDayStartTime } from "@/modules/bookings/constants";
import {
  getDateFromMinutesOfDay,
  getMinutesOfDayFromTime,
  parseTimeslotAsAmountMinutes,
} from "@/modules/bookings/helpers";
import { useTranslation } from "@/translations";

import type { CreateOrEditAssetFormValues } from "../types";

interface AssetBookableDaySelectorProps {
  bookableDay: BookableDay;
  bookableDayIndex: number;
  onChangeStartTime: (index: number, value: Date | undefined) => void;
  onChangeEndTime: (index: number, value: Date | undefined) => void;
}
export function AssetBookableDaySelector({
  bookableDay,
  bookableDayIndex,
  onChangeStartTime,
  onChangeEndTime,
}: AssetBookableDaySelectorProps): React.ReactNode {
  const { t } = useTranslation();
  const formMethods = useFormContext<CreateOrEditAssetFormValues>();
  const { fields, append, remove } = useFieldArray({
    control: formMethods.control,
    name: `bookableDays.${bookableDayIndex}.times`,
  });
  const timeslot = useWatch({
    control: formMethods.control,
    name: "timeslot",
  });
  const customTimeslot = useWatch({
    control: formMethods.control,
    name: "customTimeslot",
  });
  const watchedBookableTimes = bookableDay.times;
  const bookableTimes = fields.map((bookableTime, index) => {
    return {
      ...bookableTime,
      ...watchedBookableTimes[index],
    };
  });
  const onClickAddBookableTime = () => {
    let newStartTime: number | null = getMinutesOfDayFromTime(defaultBookableDayStartTime, "start");
    let newEndTime: number | null = getMinutesOfDayFromTime(defaultBookableDayEndTime, "end");
    if (bookableTimes.length === 0) {
      formMethods.setValue(`bookableDays.${bookableDayIndex}.enabled`, true);
    } else {
      const lastBookableTime = bookableTimes[bookableTimes.length - 1];
      newStartTime = lastBookableTime.endTime;
      newEndTime = null;
    }
    append({
      startTime: newStartTime,
      endTime: newEndTime,
    });
  };
  const onClickRemoveBookableTime = (bookableTimeIndex: number) => {
    if (bookableTimes.length === 1) {
      formMethods.setValue(`bookableDays.${bookableDayIndex}.enabled`, false);
    }
    remove(bookableTimeIndex);
  };
  const isSelectorDisabled = !timeslot;
  const lastBookableTime = bookableTimes.length > 0 && bookableTimes[bookableTimes.length - 1];
  const isAddNewBookableTimeAllowed =
    lastBookableTime && isDefined(lastBookableTime.endTime) && lastBookableTime.endTime < 1440 && timeslot !== "allDay";
  // A time period contains zombie time if the bookable time duration is not a multiple of the selected timeslot size.
  // e.g. Timeslot = 2hr; Time period = 08:00 - 10:30; Zombie time is present (30 min).
  const isZombieTimePresent =
    timeslot &&
    timeslot !== "allDay" &&
    bookableTimes.some((currBookableTime) => {
      return (
        isDefined(currBookableTime.startTime) &&
        isDefined(currBookableTime.endTime) &&
        (currBookableTime.endTime - currBookableTime.startTime) %
          parseTimeslotAsAmountMinutes(timeslot, Number(customTimeslot || 5))! !==
          0
      );
    });

  return (
    <Columns
      data-testid="bookable-day-section"
      collapseBelow="md"
      align="start"
      gap={{ base: "sm", container: { md: "xl" } }}
    >
      <header className="flex min-h-control-md w-full flex-1 flex-col justify-center">
        <Text as="p">{t(`common.date.weekday.${bookableDay.day}`)}</Text>
        {!isSelectorDisabled && isZombieTimePresent && (
          <Text as="p" variant="caption" tone="secondary">
            {t("page.bookings.create-or-edit.form.section.booking-details.bookable-days.zombie-time-present.label")}
          </Text>
        )}
      </header>
      <div className="relative flex w-full flex-1 flex-col gap-space-sm">
        {bookableTimes.length === 0 && (
          <Button
            fullWidth
            variant="soft"
            onClick={onClickAddBookableTime}
            disabled={isSelectorDisabled}
            icon={<ClockPlusIcon />}
            data-testid="add-first-bookable-time-btn"
          >
            {t("page.bookings.create-or-edit.form.section.booking-details.bookable-days.add-opening-time.btn")}
          </Button>
        )}
        {bookableTimes.length > 0 &&
          bookableTimes.map((currBookableTime, bookableTimeIndex) => {
            const timePickerStep = 15; // 15 minutes
            const isTimePickerDisabled = !bookableDay.enabled || isSelectorDisabled;
            const islastBookableTime = bookableTimeIndex === bookableTimes.length - 1;
            const prevBookableTime = bookableTimes.length > 1 && bookableTimes[bookableTimeIndex - 1];

            return (
              <Inline key={currBookableTime.id} gap="sm" align="center" wrap={false}>
                {/* Start time */}
                <div className="w-full max-w-control-narrow">
                  <FormDatePicker<
                    CreateOrEditAssetFormValues,
                    `bookableDays.${typeof bookableDayIndex}.times.${typeof bookableTimeIndex}.startTime`
                  >
                    name={`bookableDays.${bookableDayIndex}.times.${bookableTimeIndex}.startTime`}
                    type="time"
                    min={
                      prevBookableTime && prevBookableTime.endTime
                        ? getDateFromMinutesOfDay(prevBookableTime.endTime)
                        : undefined
                    }
                    minuteStep={timePickerStep}
                    onChange={(value) => onChangeStartTime(bookableTimeIndex, value)}
                    rules={{
                      validate: {
                        required: (startTime) => {
                          if (!isDefined(startTime) && bookableDay.enabled && !bookableDay.allDay) {
                            return "";
                          }
                        },
                        soonerThanEndTime: (startTime) => {
                          if (
                            isDefined(startTime) &&
                            isDefined(currBookableTime.endTime) &&
                            startTime >= currBookableTime.endTime
                          ) {
                            return "";
                          }
                        },
                        longerThanTimeslot: (startTime, values) => {
                          if (
                            values.timeslot !== "allDay" &&
                            isDefined(startTime) &&
                            isDefined(currBookableTime.endTime) &&
                            values.timeslot &&
                            currBookableTime.endTime - startTime <
                              parseTimeslotAsAmountMinutes(values.timeslot, Number(values.customTimeslot || 5))!
                          ) {
                            return "";
                          }
                        },
                      },
                    }}
                    data-testid="start-time-picker-input"
                    disabled={isTimePickerDisabled}
                  />
                </div>
                <label className="text-nowrap">
                  {t("page.bookings.create-or-edit.form.section.booking-details.bookable-days.days.to.label")}
                </label>
                {/* End time */}
                <div className="w-full max-w-control-narrow">
                  <FormDatePicker<
                    CreateOrEditAssetFormValues,
                    `bookableDays.${typeof bookableDayIndex}.times.${typeof bookableTimeIndex}.endTime`
                  >
                    name={`bookableDays.${bookableDayIndex}.times.${bookableTimeIndex}.endTime`}
                    type="time"
                    minuteStep={timePickerStep}
                    onChange={(value) => onChangeEndTime(bookableTimeIndex, value)}
                    rules={{
                      validate: {
                        required: (endTime) => {
                          if (!isDefined(endTime) && bookableDay.enabled && !bookableDay.allDay) {
                            return "";
                          }
                        },
                        laterThanStartTime: (endTime: number | null) => {
                          if (
                            isDefined(currBookableTime.startTime) &&
                            isDefined(endTime) &&
                            currBookableTime.startTime >= endTime
                          ) {
                            return "";
                          }
                        },
                        longerThanTimeslot: (endTime, values) => {
                          if (
                            values.timeslot !== "allDay" &&
                            isDefined(endTime) &&
                            isDefined(currBookableTime.startTime) &&
                            values.timeslot &&
                            endTime - currBookableTime.startTime <
                              parseTimeslotAsAmountMinutes(values.timeslot, Number(values.customTimeslot || 5))!
                          ) {
                            return "";
                          }
                        },
                      },
                    }}
                    data-testid="end-time-picker-input"
                    disabled={isTimePickerDisabled}
                  />
                </div>
                <IconButton
                  variant="soft"
                  title={t(
                    "page.bookings.create-or-edit.form.section.booking-details.bookable-days.days.remove-opening-time.btn.tooltip",
                  )}
                  onClick={() => onClickRemoveBookableTime(bookableTimeIndex)}
                  withTooltip={false}
                  disabled={isSelectorDisabled}
                  data-testid="delete-bookable-time-btn"
                  icon={<Trash01Icon />}
                />
                <div className={isAddNewBookableTimeAllowed && islastBookableTime ? "visible" : "invisible"}>
                  <IconButton
                    aria-hidden={!isAddNewBookableTimeAllowed || !islastBookableTime}
                    variant="soft"
                    title={t(
                      "page.bookings.create-or-edit.form.section.booking-details.bookable-days.days.add-opening-time.btn.tooltip",
                    )}
                    onClick={onClickAddBookableTime}
                    withTooltip={false}
                    disabled={isSelectorDisabled}
                    data-testid="add-bookable-time-btn"
                    icon={<ClockPlusIcon />}
                  />
                </div>
              </Inline>
            );
          })}
      </div>
    </Columns>
  );
}
